/**
 * The badge shelf ([Achievements](../../../docs/ACHIEVEMENTS.md)).
 *
 * The whole screen is one rule: **a child must never open this and read a list
 * of ways they are not good enough** ([D-031](../../../docs/DECISIONS.md#d-031),
 * [D-106](../../../docs/DECISIONS.md#d-106)). Three things carry that.
 *
 * - **There is no count, and nothing to count with.** The server sends entries
 *   and no total, so *"7 of 45"* is not a number this file could render if it
 *   wanted to. `.length` is never taken of anything on this screen.
 * - **A locked badge is a soft silhouette and a hint**, phrased as an
 *   invitation — *"Finish a chapter without any mistakes"*. Not greyed out with
 *   a padlock, not "not achieved", not a progress bar toward it.
 * - **What they have done comes first.** The server sorts earned first and
 *   newest at the top, so the top of the screen is always something that went
 *   well. The rest read as a path rather than a scoreboard.
 *
 * The titles strip is here rather than on the wardrobe because a title is
 * earned, not bought-and-worn, and the thing that grants it is on this screen.
 */

import { titleById } from '@capytalk/core';
import { useEffect, useState } from 'react';
import { type Client, type Trouble, troubleFrom } from './api.js';
import Sneakers from './Sneakers.js';

interface Entry {
  readonly id: string;
  readonly nameL1: string;
  readonly hint: string;
  readonly badge: string;
  readonly family: string;
  readonly earned: boolean;
  readonly at: string | Date | null;
}

interface Shelf {
  readonly entries: readonly Entry[];
  readonly titles: readonly string[];
}

/**
 * How many still-ahead badges each family shows.
 *
 * A judgement call, and worth saying out loud: with nothing earned this screen
 * rendered **forty-five greyed rows**, which is a long scroll and reads as a
 * to-do list however kindly each line is phrased — close to the thing
 * [D-031](../../../docs/DECISIONS.md#d-031) exists to prevent. The design says
 * the catalogue is there "to show what they *have* done and to hint at what is
 * possible **next**", and three per family is that hint; forty-five is an
 * inventory.
 *
 * Only visible in a browser at phone width. The component tests were green and
 * had no opinion.
 */
const AHEAD_PER_FAMILY = 3;

/** The families, in the words a child reads above them. */
const FAMILY_NAMES: Readonly<Record<string, string>> = {
  progress: 'Getting on',
  persistence: 'Keeping at it',
  skill: 'Getting good',
  exploration: 'Looking around',
};

function Badge({ entry }: { readonly entry: Entry }) {
  return (
    <li
      className="card"
      style={{
        marginTop: 8,
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        // Softer, never greyer: a locked badge is a thing that has not happened
        // yet, and a disabled-looking row reads as a thing that went wrong.
        opacity: entry.earned ? 1 : 0.72,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          fontSize: 34,
          // The silhouette. Brightness rather than greyscale keeps the shape
          // readable, which is the point of a silhouette: you can tell what it
          // is going to be.
          filter: entry.earned ? 'none' : 'grayscale(1) opacity(0.55)',
        }}
      >
        {entry.badge}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 800 }}>{entry.nameL1}</div>
        {/*
          The hint is shown for both. On an earned badge it says what they did;
          on one still ahead it says what to try. Hiding it once earned would
          leave a child looking at a name with no story behind it.
        */}
        <p className="tiny" style={{ marginTop: 2 }}>
          {entry.hint}
        </p>
      </div>
      {entry.earned && (
        <span aria-label="You have this one" style={{ fontSize: 20 }}>
          ✓
        </span>
      )}
    </li>
  );
}

export default function Badges({ client }: { readonly client: Client }) {
  const [shelf, setShelf] = useState<Shelf | null>(null);
  const [trouble, setTrouble] = useState<Trouble | null>(null);

  useEffect(() => {
    let live = true;
    // A server one deploy behind has no `achievements` router and the call
    // throws synchronously ([D-066](../../../docs/DECISIONS.md#d-066)).
    Promise.resolve()
      .then(() => client.achievements.shelf.query())
      .then((next) => {
        if (live) setShelf(next);
      })
      .catch((error: unknown) => {
        if (live) setTrouble(troubleFrom(error));
      });
    return () => {
      live = false;
    };
  }, [client]);

  if (trouble !== null) {
    return (
      <div className="grow pad center">
        <p className="sub" role="alert">
          {trouble.kind === 'offline'
            ? 'No connection. Is the wi-fi on?'
            : 'Your badges would not load.'}
        </p>
      </div>
    );
  }

  if (shelf === null) {
    return (
      <div className="grow pad center">
        <p className="sub">Finding your badges…</p>
      </div>
    );
  }

  const earned = shelf.entries.filter((entry) => entry.earned);
  const families = ['progress', 'persistence', 'skill', 'exploration'];

  return (
    <div className="grow pad">
      <Sneakers
        text={
          /*
            Never a number, and never a comparison. "You have three" invites
            "out of how many?", which is the question this whole screen exists
            to not answer ([D-031](../../../docs/DECISIONS.md#d-031)).
          */
          earned.length === 0
            ? 'Badges turn up as you go. Here is what there is to find!'
            : 'Look what you have done!'
        }
        audioRef={null}
        size={72}
      />

      {shelf.titles.length > 0 && (
        <section style={{ marginTop: 14 }}>
          <h3>Your titles</h3>
          <p className="tiny" style={{ marginTop: 4 }}>
            Titles you have. Wear one under your name from Me or the shop.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {shelf.titles.map((id) => (
              <span key={id} className="pill">
                {titleById(id)?.textL1 ?? id.replace(/^title\./, '').replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </section>
      )}

      {earned.length > 0 && (
        <section style={{ marginTop: 14 }}>
          <h3>What you have done</h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {earned.map((entry) => (
              <Badge key={entry.id} entry={entry} />
            ))}
          </ul>
        </section>
      )}

      {/*
        Everything still ahead, grouped by family so it reads as four short
        lists of things to try rather than one long list of things undone.
      */}
      {families.map((family) => {
        const all = shelf.entries.filter(
          (entry) => !entry.earned && entry.family === family,
        );
        if (all.length === 0) return null;
        const ahead = all.slice(0, AHEAD_PER_FAMILY);
        return (
          <section key={family} style={{ marginTop: 14 }}>
            <h3>{FAMILY_NAMES[family] ?? family}</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {ahead.map((entry) => (
                <Badge key={entry.id} entry={entry} />
              ))}
            </ul>
            {all.length > ahead.length && (
              // Never a number. "And 9 more" is the count this whole screen
              // exists to not show, wearing a different hat.
              <p className="tiny" style={{ marginTop: 6 }}>
                …and more to find.
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
}
